/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import type { ScanGroupOptimized, ScanRootOptimized, ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';
import { useEffect, useState } from 'react';

import { ADMIN_LEFT_NAV_WIDTH } from '@/Client/Layout/layoutConstants';
import { useToast } from '@/Client/State';
import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { styleguideControlSizes as CONTROL_SIZES, styleguideFontFamily as FONT, withOpacity } from '@kaiizen/kai-shiki/core';
import { LeftIcon } from '@kaiizen/kai-shiki/react';

import { getGroupTitle } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { getGalleryOperationToast } from '../GalleryPhotoManagement';
import {
  contextsAreEqual,
  type GalleryPhotoOccurrence,
  type GalleryPhotoOperation,
  getGalleryPhotoOccurrenceKey,
} from '../galleryPhotoOperations';
import { buildPhotoManagerModel, type PhotoManagerScope, type PhotoManagerSection } from '../photoManagerModel';
import { PhotoManagerDetailOverlay } from './PhotoManagerDetailOverlay';
import { buildOccurrence, getPhotoCountLabel } from './photoManagerLib';
import { PhotoManagerSectionView } from './PhotoManagerSectionView';
import { ACTION_BUTTON_STYLE, SECTION_COUNT_STYLE } from './photoManagerStyles';

/**
 * The Photo Manager (KAIDEV-3647): photo organization as a full-bleed dark takeover, launched
 * from the gear on the Editor tab's photo strip. The gallery logic is lifted from the route-only
 * FocusedPhotoGallery; the experience follows the mockup — the section you drop into is the
 * destination, selection acts from a bottom bar, and nothing here opens a second modal: even
 * delete confirms in the bar, a white card fighting the dark takeover would read as a bug.
 */

const SCRIM_STYLE = css`
  label: PHOTO_MANAGER_SCRIM;
  position: fixed;
  right: 0;
  bottom: 0;
  /* Inset from the app chrome rather than covering it: the admin left nav is fixed at z 1000, and
     MainNavigation is a topBarHeight-tall bar above the content. The takeover fills what is left
     — the work surface — so the project's own nav stays reachable while it is open. */
  top: ${CONTROL_SIZES.topBarHeight}px;
  left: ${pxToRem(ADMIN_LEFT_NAV_WIDTH)};
  z-index: 400;
  background: ${COLORS.ink};
  color: ${COLORS.white};
  display: flex;
  flex-direction: column;
`;

const HEADER_STYLE = css`
  label: PHOTO_MANAGER_HEADER;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 18px 24px;
  border-bottom: 1px solid ${withOpacity(COLORS.white, 0.14)};
`;

/* Mockup `.pm-back`. Sits beside the title rather than above it, so photo mode costs the header
   no extra row and it keeps its shape when a photo opens. */
const BACK_BUTTON_STYLE = css`
  label: PHOTO_MANAGER_BACK;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px 5px 7px;
  background: none;
  border: 1px solid ${withOpacity(COLORS.white, 0.22)};
  color: ${COLORS.white};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    background: ${withOpacity(COLORS.white, 0.1)};
    border-color: ${withOpacity(COLORS.white, 0.5)};
  }
`;

const TITLE_STYLE = css`
  font-family: ${FONT.display};
  font-size: ${pxToRem(20)};
  font-weight: 700;
  line-height: 1.2;
`;

const COUNT_STYLE = css`
  flex: 0 0 auto;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(12)};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SHOW_EMPTY_TOGGLE_STYLE = css`
  label: PHOTO_MANAGER_SHOW_EMPTY;
  flex: 0 0 auto;
  min-height: 28px;
  padding: 0 12px;
  background: none;
  border: 1px solid ${withOpacity(COLORS.white, 0.3)};
  color: ${withOpacity(COLORS.white, 0.62)};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(11)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    border-color: ${COLORS.white};
    color: ${COLORS.white};
  }

  &[aria-pressed='true'] {
    background: ${COLORS.white};
    border-color: ${COLORS.white};
    color: ${COLORS.ink};
  }
`;

const CLOSE_BUTTON_STYLE = css`
  label: PHOTO_MANAGER_CLOSE;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: none;
  border: 1px solid ${withOpacity(COLORS.white, 0.22)};
  color: ${COLORS.white};
  cursor: pointer;
  font-size: ${pxToRem(14)};

  &:hover {
    background: ${withOpacity(COLORS.white, 0.1)};
    border-color: ${withOpacity(COLORS.white, 0.5)};
  }
`;

const BODY_STYLE = css`
  label: PHOTO_MANAGER_BODY;
  flex: 1;
  overflow-y: auto;
  padding: 22px 24px 90px;
`;

const ACTION_BAR_STYLE = css`
  label: PHOTO_MANAGER_ACTION_BAR;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 24px;
  border-top: 1px solid ${withOpacity(COLORS.white, 0.14)};
  background: ${COLORS.ink};
`;

const ACTION_BAR_COUNT_STYLE = css`
  font-family: ${FONT.mono};
  font-size: ${pxToRem(12)};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const DANGER_BUTTON_STYLE = css`
  ${ACTION_BUTTON_STYLE}
  border-color: ${COLORS.error};
  color: ${COLORS.error};
`;

const MENU_WRAP_STYLE = css`
  position: relative;
`;

const MENU_STYLE = css`
  label: PHOTO_MANAGER_MENU;
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  min-width: 240px;
  max-height: 320px;
  overflow-y: auto;
  background: ${COLORS.ink};
  border: 1px solid ${withOpacity(COLORS.white, 0.22)};
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SEGMENT_STYLE = css`
  display: inline-flex;
  border: 1px solid ${withOpacity(COLORS.white, 0.3)};

  button {
    flex: 1;
    min-height: 28px;
    padding: 0 12px;
    background: none;
    border: 0;
    color: ${withOpacity(COLORS.white, 0.62)};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(11)};
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    cursor: pointer;
  }

  button[aria-pressed='true'] {
    background: ${COLORS.white};
    color: ${COLORS.ink};
  }
`;

const MENU_TARGET_STYLE = css`
  display: flex;
  align-items: center;
  min-height: 30px;
  padding: 0 10px;
  background: none;
  border: 0;
  color: ${COLORS.white};
  font-family: ${FONT.body};
  font-size: ${pxToRem(13)};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${withOpacity(COLORS.white, 0.1)};
  }
`;

/** The title of the group a staged photo hangs off, for the overlay's subtitle. */
const getStageGroupTitle = (
  groups: readonly ScanGroupOptimized[],
  occurrence: GalleryPhotoOccurrence,
): string | undefined => {
  const group = groups.find(candidate => candidate.id === occurrence.context.groupId);
  return group ? getGroupTitle(group) : undefined;
};

/** The rest of the photos on the group this one hangs off — the overlay's group rail. */
/** The task a photo hangs off, which every cost row in the detail pane is derived from. */
const getStageTask = (
  groups: readonly ScanGroupOptimized[],
  occurrence: GalleryPhotoOccurrence,
): ScanTaskOptimized | undefined => {
  const { groupId, taskId } = occurrence.context;
  if (!groupId || !taskId) return undefined;
  const group = groups.find(candidate => candidate.id === groupId);
  return group?.tasksById?.[taskId] ?? group?.tasks?.find(candidate => candidate.id === taskId);
};

/** The photo's own section, which the overlay's first rail is scoped to. */
const getStageSectionPhotos = (
  sections: readonly PhotoManagerSection[],
  occurrence: GalleryPhotoOccurrence,
): readonly GalleryPhotoOccurrence[] => {
  const own = sections.find(section => contextsAreEqual(section.scanPartContext, occurrence.context));
  return own ? own.photos.map(photo => buildOccurrence(photo, own)) : [];
};

const getStageGroupPhotos = (
  sections: readonly PhotoManagerSection[],
  occurrence: GalleryPhotoOccurrence,
): readonly GalleryPhotoOccurrence[] => {
  const groupSection = sections.find(section => section.kind === 'group' && section.scanPartContext.groupId === occurrence.context.groupId);
  if (!groupSection) return [];
  return groupSection.photos.map(photo => buildOccurrence(photo, groupSection));
};

/** The occurrence key for a photo the takeover was opened on, if it is in scope at all. */
const buildInitialStageKey = (
  sections: readonly PhotoManagerSection[],
  photoId: string,
): string | null => {
  for (const section of sections) {
    const photo = section.photos.find(candidate => candidate.photoId === photoId);
    if (photo) return getGalleryPhotoOccurrenceKey({ ...section.scanPartContext, photoId });
  }
  return null;
};

/** How the error toast names what was attempted: "Failed to update the date of this photo". */
const getStageOperationVerb = (type: GalleryPhotoOperation['type']): string => type === 'setDate' ? 'update the date of' : type;

/** Escape belongs to whatever is being typed into, if anything is. */
const isTextEntry = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
};

export const PhotoManagerModal = ({
  scanRoot,
  groups,
  scope,
  applyPhotoOperation,
  scanIsEditable = true,
  initialPhotoId,
  onClose,
}: {
  scanRoot: ScanRootOptimized;
  groups: readonly ScanGroupOptimized[];
  scope: PhotoManagerScope;
  applyPhotoOperation: (operation: GalleryPhotoOperation) => void | Promise<void>;
  scanIsEditable?: boolean;
  /** Open straight onto this photo's detail — the strip hands the takeover the photo that was
   *  clicked, instead of routing away to the progress panel to show it. */
  initialPhotoId?: string;
  onClose: () => void;
}) => {
  const model = buildPhotoManagerModel({ scanRoot, groups, ...scope });
  const [selectedOccurrences, setSelectedOccurrences] = useState<ReadonlyMap<string, GalleryPhotoOccurrence>>(new Map());
  const [menuOpen, setMenuOpen] = useState(false);
  const [moveMode, setMoveMode] = useState<'move' | 'copy'>('move');
  const [deleteConfirming, setDeleteConfirming] = useState(false);
  /* Seeded once. `initialPhotoId` says which photo the takeover was opened on; after that the
     overlay is the user's to navigate, so a later prop change must not yank them elsewhere. */
  const [stagePhotoKey, setStagePhotoKey] = useState<string | null>(
    () => initialPhotoId ? buildInitialStageKey(model.sections, initialPhotoId) : null,
  );
  /* Escape and the header's close both unwind one level at a time — photo, then the takeover —
     which is the mockup's keyboard model. Opened photo-first from the editor's strip there is no
     grid behind the photo, so the first level out is the takeover itself. */
  const canReturnToGrid = stagePhotoKey != null && !initialPhotoId;
  const closeOneLevel = () => canReturnToGrid ? setStagePhotoKey(null) : onClose();

  // Off by default: vacant groups and tasks stay hidden until asked for — the Christine call.
  const [showEmpty, setShowEmpty] = useState(false);
  const { showToast, showErrorToast } = useToast();
  /* Internal testing control: placing a photo on any day, to exercise date-driven behaviour
     without waiting for real time to pass. Off for everyone the flag is not on for. */
  const { featureFlags } = useFeatureFlags();
  const canEditPhotoDate = scanIsEditable && featureFlags.testingTools;

  // The takeover is a modal: the page underneath keeps its scroll position, so closing
  // needs no restoration — just the lock's release.
  useEffect(() => {
    const unwind = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // A control with its own Escape — a label draft, a picker's filter — cancels itself first.
      // Taking the key ahead of it would unwind the takeover out from under an open editor.
      if (isTextEntry(event.target)) return;
      event.stopPropagation();
      closeOneLevel();
    };
    document.addEventListener('keydown', unwind);
    return () => document.removeEventListener('keydown', unwind);
  });

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const toggleSelection = (occurrence: GalleryPhotoOccurrence) => {
    const next = new Map(selectedOccurrences);
    if (!next.delete(occurrence.key)) next.set(occurrence.key, occurrence);
    setSelectedOccurrences(next);
    if (!next.size) {
      setMenuOpen(false);
      setDeleteConfirming(false);
    }
  };

  const clearSelection = () => {
    setSelectedOccurrences(new Map());
    setMenuOpen(false);
    setDeleteConfirming(false);
  };

  /**
   * Mirrors the route gallery's `applyOperation`: the selection survives a failure so it can be
   * retried, and a rejected `setScan` reaches the user as a toast rather than an unhandled
   * rejection in the console.
   */
  const applyToSelection = async (operation: GalleryPhotoOperation) => {
    const selectedCount = selectedOccurrences.size;
    try {
      await applyPhotoOperation(operation);
      clearSelection();
      showToast(getGalleryOperationToast(selectedCount, operation.type));
    } catch (error) {
      showErrorToast(error, `${operation.type} selected photo assignments`);
    }
  };

  const sources = [...selectedOccurrences.values()];
  const targets = model.sections.filter(section => section.acceptsPhotos);

  /**
   * What the toggle filters. Two sections are exempt: the anchor (the scope whose gear opened
   * the manager — hiding a vacant task you opened from would strand its only add affordance)
   * and Unsorted, whose whole purpose is being the empty destination. Destinations for
   * move/copy come from the full model either way: a section need not be visible to receive.
   */
  const anchorKey = scope.taskId ? `task:${scope.taskId}` : scope.groupId ? `group:${scope.groupId}` : undefined;

  /**
   * Hiding the vacant sections is only a choice worth offering when some are vacant and some
   * are not. With every section full there is nothing to hide; with none of them holding a
   * photo, hiding them empties the manager and takes the only way to add one with it — which
   * is exactly the state you arrive in from an empty photo strip. Either way the filter is
   * inert, so the manager shows everything and says so by disabling the control.
   */
  const sectionsWithPhotos = model.sections.filter(section => section.photos.length > 0);
  const canHideEmptySections = sectionsWithPhotos.length > 0 && sectionsWithPhotos.length < model.sections.length;
  const showingEmptySections = canHideEmptySections ? showEmpty : sectionsWithPhotos.length === 0;
  const visibleSections = showEmpty || !canHideEmptySections
    ? model.sections
    : model.sections.filter(section => section.photos.length > 0 || section.kind === 'unsorted' || section.key === anchorKey);

  // The stage's world: every photo the modal shows, flattened in section order.
  const flatPhotos = model.sections.flatMap(section => section.photos.map(photo => buildOccurrence(photo, section)));
  const stageIndex = stagePhotoKey == null ? -1 : flatPhotos.findIndex(occurrence => occurrence.key === stagePhotoKey);
  const stageOccurrence = stageIndex >= 0 ? flatPhotos[stageIndex] : undefined;

  /* Photo mode acts on the photo on screen, not on the grid's selection. Mockup
     `poMediaActionsHtml` reuses the takeover's own confirm and move menu for exactly this, so the
     footer is those controls pointed at a single occurrence. */
  const stageSources = stageOccurrence ? [stageOccurrence] : [];

  /* Deleting or moving the photo on screen changes the occurrence the stage is keyed on, so the
     takeover falls back to the grid on its own once the operation lands. */
  const applyToStagePhoto = async (operation: GalleryPhotoOperation) => {
    try {
      await applyPhotoOperation(operation);
      setDeleteConfirming(false);
      setMenuOpen(false);
      showToast(getGalleryOperationToast(1, operation.type));
    } catch (error) {
      showErrorToast(error, `${getStageOperationVerb(operation.type)} this photo`);
    }
  };

  /** Mockup `poMediaActionsHtml`: the footer acts on the media in front of you and nothing else. */
  const stagePhotoActions = stageSources.length
    ? deleteConfirming
      ? (
        <>
          <span css={ACTION_BAR_COUNT_STYLE}>Delete this photo?</span>
          <span css={css`flex: 1;`} />
          <button css={ACTION_BUTTON_STYLE} onClick={() => setDeleteConfirming(false)} type='button'>Cancel</button>
          <button
            css={DANGER_BUTTON_STYLE}
            onClick={() => applyToStagePhoto({ type: 'remove', sources: stageSources })}
            type='button'
          >
            Delete
          </button>
        </>
      )
      : (
        <>
          <button css={DANGER_BUTTON_STYLE} onClick={() => setDeleteConfirming(true)} type='button'>Delete</button>
          <span css={MENU_WRAP_STYLE}>
            <button css={ACTION_BUTTON_STYLE} onClick={() => setMenuOpen(open => !open)} type='button'>
              Move or copy
            </button>
            {menuOpen && (
              <div css={MENU_STYLE}>
                <span css={SEGMENT_STYLE}>
                  <button aria-pressed={moveMode === 'move'} onClick={() => setMoveMode('move')} type='button'>Move</button>
                  <button aria-pressed={moveMode === 'copy'} onClick={() => setMoveMode('copy')} type='button'>Copy</button>
                </span>
                {targets.map(section => (
                  <button
                    css={MENU_TARGET_STYLE}
                    key={section.key}
                    onClick={() => applyToStagePhoto({ type: moveMode, sources: stageSources, target: section.scanPartContext })}
                    type='button'
                  >
                    {moveMode === 'move' ? 'Move' : 'Copy'} to {section.title}
                  </button>
                ))}
              </div>
            )}
          </span>
        </>
      )
    : undefined;

  return (
    <div
      aria-modal='true'
      css={SCRIM_STYLE}
      role='dialog'
      // The editor underneath wraps itself in a PhotoDropZone; without this shield a drop on
      // modal chrome would bubble there and upload to whatever part happens to be behind the
      // modal. Section drop zones stopPropagation on their own, so only chrome lands here.
      onDragOver={event => event.stopPropagation()}
      onDrop={event => event.stopPropagation()}
    >
      <header css={HEADER_STYLE}>
        {
          /* Photo mode keeps the takeover's own title and count — the photo is a view of the same
            set, not a different one — and trades the section filter for a way back to the grid.
            Opened photo-first from the strip there is no grid behind it, so there is no way back. */
        }
        {stageOccurrence && !initialPhotoId && (
          <button css={BACK_BUTTON_STYLE} onClick={closeOneLevel} type='button'>
            <LeftIcon size={12} aria-hidden='true' />
            All photos
          </button>
        )}
        <span css={TITLE_STYLE} role='heading' aria-level={1}>{model.title}</span>
        <span css={COUNT_STYLE}>{getPhotoCountLabel(model.photoCount)}</span>
        <span css={css`flex: 1;`} />
        {!stageOccurrence && (
          <button
            aria-pressed={showingEmptySections}
            css={SHOW_EMPTY_TOGGLE_STYLE}
            disabled={!canHideEmptySections}
            onClick={() => setShowEmpty(show => !show)}
            type='button'
          >
            Show empty
          </button>
        )}
        <button aria-label='Close' css={CLOSE_BUTTON_STYLE} onClick={closeOneLevel} type='button'>✕</button>
      </header>
      {stageOccurrence
        ? (
          <PhotoManagerDetailOverlay
            groupPhotos={stageOccurrence.context.taskId
              ? getStageGroupPhotos(model.sections, stageOccurrence)
              : undefined}
            sectionPhotos={getStageSectionPhotos(model.sections, stageOccurrence)}
            task={getStageTask(groups, stageOccurrence)}
            groupTitle={getStageGroupTitle(groups, stageOccurrence)}
            occurrence={stageOccurrence}
            occurrences={flatPhotos}
            footer={stagePhotoActions}
            onSetPhotoDate={canEditPhotoDate
              ? addedAt => applyToStagePhoto({ type: 'setDate', sources: stageSources, addedAt })
              : undefined}
            onClose={closeOneLevel}
          />
        )
        : (
          <div css={BODY_STYLE}>
            {visibleSections.map(section => (
              <PhotoManagerSectionView
                key={section.key}
                onOpenPhoto={occurrence => setStagePhotoKey(occurrence.key)}
                onToggleSelection={toggleSelection}
                scanIsEditable={scanIsEditable}
                section={section}
                selectedOccurrences={selectedOccurrences}
              />
            ))}
          </div>
        )}
      {!stageOccurrence && sources.length
        ? (
          <div aria-label='Photo selection actions' css={ACTION_BAR_STYLE} role='toolbar'>
            <span css={ACTION_BAR_COUNT_STYLE}>{sources.length} selected</span>
            <span css={css`flex: 1;`} />
            {deleteConfirming
              ? (
                <>
                  <span css={SECTION_COUNT_STYLE}>Delete {getPhotoCountLabel(sources.length)}?</span>
                  <button css={ACTION_BUTTON_STYLE} onClick={() => setDeleteConfirming(false)} type='button'>Cancel</button>
                  <button
                    css={DANGER_BUTTON_STYLE}
                    onClick={() => applyToSelection({ type: 'remove', sources })}
                    type='button'
                  >
                    Confirm delete
                  </button>
                </>
              )
              : (
                <>
                  <button css={DANGER_BUTTON_STYLE} onClick={() => setDeleteConfirming(true)} type='button'>Delete</button>
                  <span css={MENU_WRAP_STYLE}>
                    <button css={ACTION_BUTTON_STYLE} onClick={() => setMenuOpen(open => !open)} type='button'>
                      Move or copy
                    </button>
                    {menuOpen && (
                      <div css={MENU_STYLE}>
                        <span css={SEGMENT_STYLE}>
                          <button aria-pressed={moveMode === 'move'} onClick={() => setMoveMode('move')} type='button'>Move</button>
                          <button aria-pressed={moveMode === 'copy'} onClick={() => setMoveMode('copy')} type='button'>Copy</button>
                        </span>
                        {targets.map(section => (
                          <button
                            css={MENU_TARGET_STYLE}
                            key={section.key}
                            onClick={() => applyToSelection({ type: moveMode, sources, target: section.scanPartContext })}
                            type='button'
                          >
                            {moveMode === 'move' ? 'Move' : 'Copy'} to {section.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </span>
                  <button css={ACTION_BUTTON_STYLE} onClick={clearSelection} type='button'>Deselect</button>
                </>
              )}
          </div>
        )
        : null}
    </div>
  );
};
